'use client';

import { User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { signOutAction } from '@/app/login/actions/supabase';
import type { UserInfo, UserRole } from '@/interface/user';
import { supabase } from '@/lib/supabase/client';
import { getUserClaims } from '@/lib/utils';

export interface AuthContextValue {
  userInfo?: UserInfo | null;
  user?: User | null;
  logOut: () => void;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  userInfo: null,
  logOut: () => {},
});

export const useAuth = () => useContext(AuthContext);

export interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const queryClient = useQueryClient();

  const logOut = useCallback(async () => {
    queryClient.removeQueries();
    queryClient.clear();
    sessionStorage.removeItem('printer_update_banner_dismissed');
    await signOutAction();
  }, [queryClient]);

  useEffect(() => {
    const subscription = supabase.auth.onAuthStateChange(async (event, session) => {
      switch (event) {
        case 'INITIAL_SESSION':
        case 'USER_UPDATED':
        case 'TOKEN_REFRESHED':
        case 'SIGNED_IN':
          if (!session) return;

          const claims = getUserClaims(session.access_token);
          const rawRole =
            claims?.user_role ||
            session.user?.user_metadata?.role ||
            session.user?.user_metadata?.app_role ||
            session.user?.user_metadata?.user_type ||
            '';

          const nextUserInfo: UserInfo = {
            name: claims?.user_name || session.user?.user_metadata?.name || session.user?.email || '',
            role: (rawRole ? String(rawRole).toUpperCase() : '') as keyof typeof UserRole,
          };

          setUserInfo(nextUserInfo);
          setUser(session.user);
          break;
        case 'SIGNED_OUT':
          setUserInfo(null);
          setUser(null);
          break;
      }
    });

    return () => subscription.data.subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        userInfo,
        logOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
